import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useBills } from '@/hooks/useBills';
import { useBillQueue } from '@/hooks/useBillQueue';
import { useCustomers } from '@/hooks/useCustomers';
import { safeArray, safeMap } from '@/utils/safeArray';
import { CheckCircle, Save, FileText, AlertCircle } from 'lucide-react';

interface SimpleManualParserProps {
  queueItem: any;
  onComplete: () => void;
  onSave: () => void;
}

export const SimpleManualParser = ({ queueItem, onComplete, onSave }: SimpleManualParserProps) => {
  const { toast } = useToast();
  const { createBill } = useBills();
  const { updateQueueItem } = useBillQueue();
  const { customers, isLoading } = useCustomers();
  
  const [formData, setFormData] = useState({
    customer_name: queueItem.customer_name_raw || '',
    account_number: queueItem.account_number_raw || '',
    amount: queueItem.amount?.toString() || '',
    bill_date: queueItem.bill_date || '',
    due_date: '',
    bill_number: queueItem.bill_number || '',
    supplier_name: queueItem.supplier_name || '',
    service_type: queueItem.service_type || 'electricity',
    billing_period_start: '',
    billing_period_end: '',
    usage_amount: '',
    usage_unit: '',
    notes: queueItem.notes || ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const required = ['customer_name', 'amount', 'bill_date'];
    const missing = required.filter(field => !formData[field as keyof typeof formData]);
    
    if (missing.length > 0) {
      toast({
        title: "Validation Error",
        description: `Please fill in required fields: ${missing.join(', ')}`,
        variant: "destructive",
      });
      return false;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Validation Error",
        description: "Amount must be a valid positive number",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setIsProcessing(true);
    try {
      // Update queue item with manual data
      await updateQueueItem({
        id: queueItem.id,
        data: {
          extracted_data: {
            ...queueItem.extracted_data,
            ...formData,
            manually_entered: true,
            confidence_override: 100
          },
          amount: parseFloat(formData.amount),
          bill_date: formData.bill_date,
          supplier_name: formData.supplier_name,
          bill_number: formData.bill_number,
          processing_status: 'review_required',
          manual_review_required: true,
          ai_confidence: 100, // Manual entry = 100% confidence
          notes: (queueItem.notes || '') + ' [MANUALLY UPDATED]'
        }
      });

      toast({
        title: "Progress Saved",
        description: "Manual data has been saved to the queue item",
      });

      onSave();
    } catch (error: any) {
      toast({
        title: "Save Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setIsProcessing(true);
    try {
      // Update queue item with manual data first
      await updateQueueItem({
        id: queueItem.id,
        data: {
          extracted_data: {
            ...queueItem.extracted_data,
            ...formData,
            manually_entered: true,
            confidence_override: 100
          },
          amount: parseFloat(formData.amount),
          bill_date: formData.bill_date,
          supplier_name: formData.supplier_name,
          bill_number: formData.bill_number,
          processing_status: 'review_required',
          manual_review_required: true,
          ai_confidence: 100,
          notes: (queueItem.notes || '') + ' [MANUALLY UPDATED]'
        }
      });

      // Then create the actual bill
      const billData = {
        account_id: selectedCustomerId || queueItem.customer_id,
        bill_date: formData.bill_date,
        amount: parseFloat(formData.amount),
        due_date: formData.due_date || undefined,
        bill_number: formData.bill_number,
        billing_period_start: formData.billing_period_start || undefined,
        billing_period_end: formData.billing_period_end || undefined,
        usage_amount: formData.usage_amount ? parseFloat(formData.usage_amount) : undefined,
        usage_unit: formData.usage_unit || undefined,
        ai_confidence: 100,
        processing_status: 'completed' as const,
        pdf_url: queueItem.file_path,
        parsed_data: {
          ...formData,
          manually_entered: true,
          file_name: queueItem.file_name,
          file_size: queueItem.file_size,
          mime_type: queueItem.mime_type,
          notes: (formData.notes || '') + ' [MANUALLY PROCESSED]'
        }
      };

      await createBill(billData);

      // Mark queue item as completed
      await updateQueueItem({
        id: queueItem.id,
        data: {
          processing_status: 'completed',
          assignment_status: 'manually_assigned',
          assigned_to_account_id: selectedCustomerId || queueItem.customer_id,
          manual_review_required: false
        }
      });

      toast({
        title: "Bill Created Successfully",
        description: "The bill has been manually processed and added to the database",
      });

      onComplete();
    } catch (error: any) {
      toast({
        title: "Processing Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Manual Bill Entry
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Manually enter the bill information below. Required fields are marked with *.
            </AlertDescription>
          </Alert>

          {/* Customer Selection - Simplified */}
          <div className="space-y-2">
            <Label htmlFor="customer_select">Customer Account</Label>
            <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder={isLoading ? "Loading customers..." : "Select customer account"} />
              </SelectTrigger>
              <SelectContent className="bg-background border shadow-lg z-50 max-h-[200px]">
                {isLoading ? (
                  <SelectItem value="loading" disabled>Loading customers...</SelectItem>
                ) : (
                  safeMap(
                    safeArray(customers).filter(customer => customer?.id && customer?.name),
                    customer => (
                      <SelectItem key={customer.id} value={customer.id} className="cursor-pointer">
                        {customer.name}
                      </SelectItem>
                    )
                  )
                )}
                {!isLoading && safeArray(customers).length === 0 && (
                  <SelectItem value="no-customers" disabled>No customers found</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Enhanced 2-Column Grid Layout - Force 2 columns */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Primary Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2">Primary Information</h4>
              
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_number">Account Number</Label>
                <Input
                  id="account_number"
                  value={formData.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  placeholder="Enter account number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bill_date">Bill Date *</Label>
                <Input
                  id="bill_date"
                  type="date"
                  value={formData.bill_date}
                  onChange={(e) => handleInputChange('bill_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                />
              </div>
            </div>

            {/* Right Column - Secondary Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2">Bill Details</h4>
              
              <div className="space-y-2">
                <Label htmlFor="bill_number">Bill Number</Label>
                <Input
                  id="bill_number"
                  value={formData.bill_number}
                  onChange={(e) => handleInputChange('bill_number', e.target.value)}
                  placeholder="Enter bill number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier_name">Supplier</Label>
                <Input
                  id="supplier_name"
                  value={formData.supplier_name}
                  onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                  placeholder="Enter supplier name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="service_type">Service Type</Label>
                <Select value={formData.service_type} onValueChange={(value) => handleInputChange('service_type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electricity">Electricity</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="internet">Internet</SelectItem>
                    <SelectItem value="waste">Waste</SelectItem>
                    <SelectItem value="sewer">Sewer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="billing_period_start">Billing Period Start</Label>
                <Input
                  id="billing_period_start"
                  type="date"
                  value={formData.billing_period_start}
                  onChange={(e) => handleInputChange('billing_period_start', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="billing_period_end">Billing Period End</Label>
                <Input
                  id="billing_period_end"
                  type="date"
                  value={formData.billing_period_end}
                  onChange={(e) => handleInputChange('billing_period_end', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Usage Information - Full Width Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2">Usage Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="usage_amount">Usage Amount</Label>
                <Input
                  id="usage_amount"
                  type="number"
                  step="0.01"
                  value={formData.usage_amount}
                  onChange={(e) => handleInputChange('usage_amount', e.target.value)}
                  placeholder="Enter usage amount"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="usage_unit">Usage Unit</Label>
                <Input
                  id="usage_unit"
                  value={formData.usage_unit}
                  onChange={(e) => handleInputChange('usage_unit', e.target.value)}
                  placeholder="kWh, gallons, etc."
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about this bill"
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              onClick={handleSave}
              disabled={isProcessing || isSubmitting}
              variant="outline"
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Save className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Progress
                </>
              )}
            </Button>

            <Button
              onClick={handleComplete}
              disabled={isProcessing || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete & Create Bill
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};